import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Layers, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useClasses } from '../../hooks/useClasses';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';

export default function SectionFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: existingSection, isLoading: loadingSection } = useQuery<any>({
    queryKey: ['section', id],
    queryFn: () => api.get(`/api/sections/${id}`).then(r => r.data),
    enabled: isEdit,
  });

  const [formData, setFormData] = useState({ classId: '', name: '', capacity: 40 });
  const [synced, setSynced] = useState(false);
  const { data: classes, isLoading: classesLoading } = useClasses();

  useEffect(() => {
    if (existingSection && !synced) {
      setFormData({
        classId: String(existingSection.class_id ?? existingSection.classId ?? ''),
        name: existingSection.name || '',
        capacity: existingSection.capacity ?? 40,
      });
      setSynced(true);
    }
  }, [existingSection, synced]);

  const createMutation = useMutation({
    mutationFn: async (payload: any) => api.post('/api/sections', payload),
    onSuccess: () => {
      toast.success('Section created');
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/sections');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create section');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: any) => api.patch(`/api/sections/${id}`, payload),
    onSuccess: () => {
      toast.success('Section updated');
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/sections');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update section');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isEdit && loadingSection) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-lime-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-lime-500" />
            {isEdit ? 'Edit Section' : 'Add Section'}
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">{isEdit ? 'Update section details' : 'Create a new section'}</p>
        </div>
      </div>

      <div className="glass-card p-4 sm:p-6 rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1">Class</Label>
            <Select value={formData.classId} onValueChange={(v) => setFormData({ ...formData, classId: v })}>
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent>
                {classesLoading ? (
                  <SelectItem value="loading" disabled>Loading...</SelectItem>
                ) : (
                  classes?.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium mb-1">Section Name</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., A"
              required
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1">Capacity</Label>
            <Input
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
              className="h-10"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="flex-1 h-10">
              {createMutation.isPending || updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isEdit ? 'Update' : 'Create'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
